import { useBlocker } from "@tanstack/react-router";
import { useForm, useSelector } from "@tanstack/react-form";
import { useAtomValue } from "jotai";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { SettingsResponse } from "@go-novus/admin-api";
import {
  AlertDialog,
  Button,
  LinkButton,
  MorphButton,
  type MorphButtonState,
  PageHeader,
  Panel,
  TabBar,
  useToast,
} from "@go-novus/ui";

import {
  readSaveLogoFailureMessage,
  readSaveSettingsFailureMessage,
  saveLogoAtom,
  saveSettingsAtom,
} from "#/features/settings";
import * as m from "#/paraglide/messages";

import {
  hasDraftErrors,
  isDraftDirty,
  readDraft,
  readDraftErrors,
  readSubmittableDraft,
  type DraftErrors,
  type SettingsDraft,
} from "../lib/settings-draft";
import { AppearanceSection } from "./appearance-section";
import { ChatWindowSection } from "./chat-window";
import { HandoffSection } from "./handoff-section";
import { LauncherSection } from "./launcher-section";
import type { DraftView } from "./preview";
import { ProactiveSection } from "./proactive";
import { StorefrontBar } from "./storefront-bar";
import { useElementPick } from "../hooks/use-element-pick";
import {
  usePreviewControl,
  type PreviewView,
} from "../hooks/use-preview-control";

/** 影响整个 Chatbox 的归 Appearance；只影响聊天窗的归 Chat window；收起入口归 Launcher；主动提示形态和主动消息规则归 Proactive messages；支持邮箱归 Human handoff。 */
export const TAB_ORDER = [
  "appearance",
  "chat_window",
  "launcher",
  "proactive",
  "handoff",
] as const;

export type SettingsTab = (typeof TAB_ORDER)[number];

export const TAB_LABEL: Record<SettingsTab, () => string> = {
  appearance: m.settings_tab_appearance,
  chat_window: m.settings_tab_chat_window,
  launcher: m.settings_tab_launcher,
  proactive: m.settings_tab_proactive,
  handoff: m.settings_tab_handoff,
};

/** 每个错误字段归到哪个标签页；DraftErrors 加键时类型检查会要求补这里。 */
const ERROR_TAB: Record<keyof DraftErrors, SettingsTab> = {
  agentName: "chat_window",
  proactiveRules: "proactive",
  rules: "proactive",
  suggestedQuestions: "chat_window",
  supportEmail: "handoff",
  themeColor: "appearance",
  welcomeMessage: "chat_window",
};

/** 切到哪个标签，预览先对齐到哪个视图；null 是不动视图（Appearance 影响所有形态，Human handoff 不显示预览）。 */
const TAB_PREVIEW_VIEW: Record<SettingsTab, PreviewView | null> = {
  appearance: null,
  chat_window: "conversation",
  launcher: "launcher",
  proactive: "proactive",
  handoff: null,
};

const SELECTED_TAB_SELECTOR = '[role="tab"][aria-selected="true"]';

export interface SettingsFormProps {
  onTabChange: (tab: SettingsTab) => void;
  saved: SettingsResponse;
  tab: SettingsTab;
}

/**
 * 草稿只活在这里，只影响预览；按下保存才整份提交。
 * `baseline` 是草稿对照的已保存配置：外部读回新值（保存后 refetch、
 * 拾取写回）先记到基线，草稿干净才整体采纳，脏草稿留给商家处理。
 *
 * Logo 是文件，塞不进那份 JSON，也就进不了 form 的草稿（`isDraftDirty` 按
 * JSON 比对），所以单独存一份。
 */
export function SettingsForm({ onTabChange, saved, tab }: SettingsFormProps) {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<MorphButtonState>("idle");
  const resetSaveFeedback = useCallback(() => {
    setSaveFeedback("idle");
  }, []);
  // 待保存的 Logo：`undefined` 没动、`file` 为 null 是移除，否则换成这张。
  // 预览地址跟着文件一起存，免得渲染时再算一次，也才好在换图时撤掉上一个。
  const [logoDraft, setLogoDraft] = useState<
    { file: File | null; url: string | null } | undefined
  >(undefined);
  const toast = useToast();
  const save = useAtomValue(saveSettingsAtom);
  const saveLogo = useAtomValue(saveLogoAtom);
  const [baseline, setBaseline] = useState(saved);
  const form = useForm({
    defaultValues: readDraft(saved),
    validators: {
      onChange: ({ value }) => {
        const validation = readDraftErrors(value);
        return hasDraftErrors(validation) ? validation : undefined;
      },
    },
    onSubmit: async ({ value }) => {
      setSaveFeedback("idle");
      // Logo 先传：它走自己的端点，不动配置的 updatedAt，这个组件不会中途被重挂。
      if (logoDraft !== undefined) {
        const uploaded = await saveLogo.execute(logoDraft.file);

        if (uploaded.isErr()) {
          setSaveFeedback("error");
          setSaveError(
            readSaveLogoFailureMessage(uploaded.error) ??
              m.settings_logo_failure_unexpected(),
          );
          return;
        }

        setLogoDraft(undefined);
      }

      // 只换了图时这份 JSON 一个字没改，不必再整份覆盖一遍。
      if (isDraftDirty(value, baseline)) {
        const result = await save.execute(readSubmittableDraft(value));

        if (result.isErr()) {
          setSaveFeedback("error");
          setSaveError(
            readSaveSettingsFailureMessage(result.error) ??
              m.settings_save_failure_unexpected(),
          );
          return;
        }

        // 回包就是刚落库的配置：草稿和基线一起对齐到它。
        form.reset(readDraft(result.value));
        setBaseline(result.value);
      }

      setSaveError(null);
      setSaveFeedback("success");
      pickSession.dismissMessages();
      toast.add({
        data: { tone: "success" },
        title: m.settings_save_success(),
      });
    },
  });
  const draft = useSelector(form.store, (state) => state.values);
  const submitting = useSelector(form.store, (state) => state.isSubmitting);
  const errors = readDraftErrors(draft);
  const pickSession = useElementPick({
    baseline,
    draft,
    form,
    logoDraftPending: logoDraft !== undefined,
    saved,
    setBaseline,
  });
  const preview = usePreviewControl(TAB_PREVIEW_VIEW[tab]);
  const dirty = isDraftDirty(draft, baseline) || logoDraft !== undefined;
  const saving = submitting || save.isPending || saveLogo.isPending;
  const blocked =
    hasDraftErrors(errors) ||
    pickSession.sessionActive ||
    pickSession.conflictPending;
  // 换下一张、清掉草稿、组件下台，都在这里把上一个 blob 地址撤掉。
  // 地址是在选文件那一下建的，首挂时还没有，StrictMode 多跑一轮也撤不到东西。
  const draftLogoUrl = logoDraft?.url ?? null;
  // 表单和预览共同读的那份草稿快照：待保存的新图优先，否则是已保存的那张。
  const draftView: DraftView = {
    agentName: draft.agent.name,
    chatbox: draft.chatbox,
    logoUrl: logoDraft === undefined ? saved.logoUrl : draftLogoUrl,
    proactiveRules: draft.proactiveRules,
  };

  useEffect(() => {
    if (draftLogoUrl === null) return;

    return () => {
      URL.revokeObjectURL(draftLogoUrl);
    };
  }, [draftLogoUrl]);
  // 标签上的红点和保存条的点名共用这份归属；Launcher 没有能出错的字段，永远轮不到它。
  const errorTabs = new Set<SettingsTab>(
    (Object.keys(errors) as (keyof DraftErrors)[]).map((key) => ERROR_TAB[key]),
  );
  // 当前标签的错误显示在字段下；保存条点名剩下出错标签里按顺序的第一个。
  const hiddenErrorTab = TAB_ORDER.find(
    (item) => item !== tab && errorTabs.has(item),
  );
  const pickBlockReason = pickSession.conflictPending
    ? m.settings_proactive_pick_conflict_blocked()
    : pickSession.sessionActive
      ? m.settings_proactive_pick_blocked()
      : undefined;
  const actionError = saveError ?? pickBlockReason;
  // 标签切换保留草稿；离开 Settings 或关页面时才询问是否丢弃修改。
  // shouldBlockFn 必须引用稳定：history.block 按函数引用反复重注册，
  // 重注册会孤儿化在途拦截的 resolver——最新草稿/会话状态走 ref。
  const blockStateRef = useRef({ dirty: false, sessionActive: false });
  useLayoutEffect(() => {
    blockStateRef.current = {
      dirty,
      sessionActive: pickSession.sessionActive,
    };
  });
  const shouldBlockLeave = useCallback(
    ({
      current,
      next,
    }: {
      current: { pathname: string };
      next: { pathname: string };
    }) =>
      (blockStateRef.current.dirty || blockStateRef.current.sessionActive) &&
      current.pathname !== next.pathname,
    [],
  );
  const leaveGuard = useBlocker({
    shouldBlockFn: shouldBlockLeave,
    enableBeforeUnload: dirty,
    withResolver: true,
  });
  // 拾取会话没结清时内部导航先拦下：按取消语义结清（关标签、重签顶掉
  // 旧令牌、重读确认）后放行；收尾失败退回本页留着失败提示可重试，
  // 不把失败当成功。草稿脏的询问在会话结清之后才弹。
  const leaveSettleStarted = useRef(false);
  useEffect(() => {
    if (leaveGuard.status !== "blocked") {
      leaveSettleStarted.current = false;
      return;
    }
    const session = pickSession.control.session;
    if (session === null) {
      if (!dirty) leaveGuard.proceed();
      return;
    }
    if (session.phase === "cancel_failed") {
      leaveGuard.reset();
      return;
    }
    if (session.phase !== "canceling" && !leaveSettleStarted.current) {
      leaveSettleStarted.current = true;
      pickSession.control.onCancelPick();
    }
  }, [dirty, leaveGuard, pickSession.control]);

  // 地址在这里建：事件回调不会被 StrictMode 多跑，不会漏出没人撤的 blob。
  function changeLogo(file: File | null): void {
    setSaveFeedback("idle");
    setLogoDraft({
      file,
      url: file === null ? null : URL.createObjectURL(file),
    });
    setSaveError(null);
  }

  function patch(next: Partial<SettingsDraft>): void {
    setSaveFeedback("idle");
    if (next.agent) form.setFieldValue("agent", next.agent);
    if (next.chatbox) form.setFieldValue("chatbox", next.chatbox);
    if (next.handoff) form.setFieldValue("handoff", next.handoff);
    if (next.proactiveRules) {
      form.setFieldValue("proactiveRules", next.proactiveRules);
    }
    setSaveError(null);
    pickSession.dismissMessages();
  }

  return (
    // 底部内边距补的是外壳滚动区的下内边距：外壳内容层是 h-full，内容溢出时那段内边距不算进滚动范围，
    // 滚到底保存条会停在内边距处、压住最后一截内容。数值跟 console-shell 的 pb-24 md:p-7 对齐。
    <form
      className="flex flex-col gap-4.5 pb-24 md:pb-7"
      onSubmit={(event) => {
        event.preventDefault();
        if (!blocked && dirty && !saving) void form.handleSubmit();
      }}
    >
      <PageHeader title={m.settings_page_title()} />
      <StorefrontBar
        enabled={draft.chatbox.storefrontEnabled}
        onChange={(storefrontEnabled) => {
          patch({ chatbox: { ...draft.chatbox, storefrontEnabled } });
        }}
        unsaved={
          draft.chatbox.storefrontEnabled !== baseline.chatbox.storefrontEnabled
        }
        themeEditorUrl={saved.themeEditorUrl}
      />
      <TabBar
        label={m.settings_tabs_label()}
        onChange={(value) => {
          const next = TAB_ORDER.find((item) => item === value);

          if (next !== undefined) {
            onTabChange(next);
          }
        }}
        tabs={TAB_ORDER.map((item) => ({
          attentionLabel: errorTabs.has(item)
            ? m.settings_tab_needs_attention()
            : undefined,
          label: TAB_LABEL[item](),
          value: item,
        }))}
        value={tab}
      />
      {tab === "appearance" && (
        <AppearanceSection
          draftView={draftView}
          errors={errors}
          onChange={(chatbox) => {
            patch({ chatbox });
          }}
          preview={preview}
        />
      )}
      {tab === "chat_window" && (
        <ChatWindowSection
          draftView={draftView}
          errors={errors}
          onAgentNameChange={(name) => {
            patch({ agent: { ...draft.agent, name } });
          }}
          onChange={(chatbox) => {
            patch({ chatbox });
          }}
          onLogoChange={changeLogo}
          preview={preview}
          saving={saving}
        />
      )}
      {tab === "launcher" && (
        <LauncherSection
          draftView={draftView}
          onChange={(chatbox) => {
            patch({ chatbox });
          }}
          preview={preview}
        />
      )}
      {tab === "proactive" && (
        <ProactiveSection
          draftView={draftView}
          errors={errors}
          onChatboxChange={(chatbox) => {
            patch({ chatbox });
          }}
          onRulesChange={(proactiveRules) => {
            patch({ proactiveRules });
          }}
          pick={pickSession.control}
          preview={preview}
        />
      )}
      {tab === "handoff" && (
        <HandoffSection
          verification={saved.handoff.supportEmailVerification}
          savedEmail={saved.handoff.supportEmail}
          errors={errors}
          handoff={draft.handoff}
          onChange={(handoff) => {
            patch({ handoff });
          }}
        />
      )}
      <div className="sticky bottom-0 z-10">
        <Panel
          padding={12}
          className="flex flex-wrap items-center gap-3 [box-shadow:var(--shadow-overlay)]"
        >
          <div className="min-w-0 flex-1 @max-[36rem]:basis-full text-micro text-[var(--content-fg-subtle)]">
            {actionError === undefined && hiddenErrorTab === undefined ? (
              m.settings_save_hint()
            ) : (
              <span
                role="alert"
                className="text-[var(--content-vibrant-error-default)]"
              >
                {actionError ??
                  (hiddenErrorTab !== undefined && (
                    <>
                      <LinkButton
                        tone="error"
                        aria-label={m.settings_error_go_to_tab({
                          tab: TAB_LABEL[hiddenErrorTab](),
                        })}
                        onClick={() => {
                          onTabChange(hiddenErrorTab);
                          // 点名成功后这个按钮会卸载，焦点掉到 body；切完把焦点还给目标标签。
                          requestAnimationFrame(() => {
                            document
                              .querySelector<HTMLElement>(SELECTED_TAB_SELECTOR)
                              ?.focus();
                          });
                        }}
                      >
                        {TAB_LABEL[hiddenErrorTab]()}
                      </LinkButton>{" "}
                      {m.settings_error_tab_fields()}
                    </>
                  ))}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2 @max-[36rem]:w-full @max-[36rem]:justify-end">
            <Button
              disabled={
                !dirty ||
                saving ||
                pickSession.sessionActive ||
                pickSession.conflictPending
              }
              type="button"
              variant="quiet"
              onClick={() => {
                form.reset(readDraft(baseline));
                setLogoDraft(undefined);
                setSaveError(null);
                setSaveFeedback("idle");
                pickSession.dismissMessages();
              }}
            >
              {m.settings_discard()}
            </Button>
            <MorphButton
              disabled={!dirty || blocked || saving}
              type="submit"
              state={saving ? "loading" : saveFeedback}
              onReset={resetSaveFeedback}
              loadingLabel={m.settings_saving()}
              successLabel={m.settings_saved()}
              errorLabel={m.settings_save_failed()}
            >
              {m.settings_save()}
            </MorphButton>
          </div>
        </Panel>
      </div>
      <AlertDialog
        cancelLabel={m.settings_leave_cancel()}
        confirmLabel={m.settings_leave_confirm()}
        description={m.settings_leave_description()}
        onCancel={() => {
          leaveGuard.reset?.();
        }}
        onConfirm={() => {
          leaveGuard.proceed?.();
        }}
        open={leaveGuard.status === "blocked" && !pickSession.sessionActive}
        title={m.settings_leave_title()}
        tone="danger"
      />
    </form>
  );
}
